/**
 * <header-glass>
 *
 * Glass header controller. Lives inside the header section and drives the
 * `#header-group` wrapper that theme.liquid renders with
 * `data-tg-glass="overlay|solid"` (server side, so there is no layout shift).
 *
 * Responsibilities
 *  - `data-tg-scrolled`: true once the page is scrolled past 50px, or while
 *    the mobile drawer / a mega-menu is open (the bar must be opaque behind an
 *    open menu). Toggled only on change.
 *  - `--tg-header-height` on <html>: the group's live height, so anchor
 *    scroll-padding and inner-page top padding share one source of truth.
 *  - Forces `solid` when the page does not open with a hero (nothing to be
 *    transparent over).
 *
 * Memory: passive listeners on a per-connection AbortSignal, one coalesced
 * requestAnimationFrame, observers disconnected on disconnect. `data-tg-glass`
 * itself is never removed here because the server owns it.
 */
class HeaderGlass extends HTMLElement {
  static THRESHOLD = 50;

  /** @type {AbortController | null} */
  #lifetime = null;

  /** @type {ResizeObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #frame = null;

  /** @type {HTMLElement | null} */
  #group = null;

  /** @type {boolean} */
  #scrolled = false;

  /** @type {number} */
  #height = -1;

  connectedCallback() {
    const group = this.closest('#header-group');
    if (!(group instanceof HTMLElement)) return;

    this.#group = group;
    this.#lifetime?.abort();
    this.#lifetime = new AbortController();
    const { signal } = this.#lifetime;

    if (!document.querySelector('main > .hero-wrapper:first-child')) {
      group.setAttribute('data-tg-glass', 'solid');
    }

    this.#observer?.disconnect();
    this.#observer = new ResizeObserver(([entry]) => {
      const size = entry?.borderBoxSize?.[0]?.blockSize ?? group.offsetHeight;
      this.#publishHeight(Math.round(size));
    });
    this.#observer.observe(group);

    window.addEventListener('scroll', this.#schedule, { passive: true, signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    // <details> toggle does not bubble; capture it from the group.
    group.addEventListener('toggle', this.#schedule, { capture: true, signal });
    group.addEventListener('focusin', this.#schedule, { signal });
    group.addEventListener('focusout', this.#schedule, { signal });

    this.#scrolled = group.getAttribute('data-tg-scrolled') === 'true';
    this.#update();
  }

  disconnectedCallback() {
    this.#lifetime?.abort();
    this.#lifetime = null;
    this.#observer?.disconnect();
    this.#observer = null;

    if (this.#frame !== null) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }

    this.#group = null;
    this.#height = -1;
    document.documentElement.style.removeProperty('--tg-header-height');
  }

  #schedule = () => {
    if (this.#frame !== null) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#update();
    });
  };

  #update() {
    const group = this.#group;
    if (!group) return;

    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;

    // Reading progress for the 2px line (fallback where scroll timelines are unsupported).
    const doc = document.documentElement;
    const range = Math.max(1, doc.scrollHeight - window.innerHeight);
    doc.style.setProperty('--tg-progress', String(Math.min(1, Math.max(0, scrollY / range)).toFixed(4)));
    const menuOpen = Boolean(
      group.querySelector('details.menu-drawer-container[open], [aria-haspopup][aria-expanded="true"]')
    );
    const scrolled = scrollY > HeaderGlass.THRESHOLD || menuOpen;

    if (scrolled === this.#scrolled && group.hasAttribute('data-tg-scrolled')) return;

    this.#scrolled = scrolled;
    group.setAttribute('data-tg-scrolled', String(scrolled));
  }

  /** @param {number} height */
  #publishHeight(height) {
    if (height === this.#height) return;

    this.#height = height;
    document.documentElement.style.setProperty('--tg-header-height', `${height}px`);
  }
}

if (!customElements.get('header-glass')) {
  customElements.define('header-glass', HeaderGlass);
}
